import { useDialog, useToast } from '@repo/ui';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMutation, useRelayEnvironment } from 'react-relay';

import { tritxRevokeCancelMutation } from '@/__generated__/tritxRevokeCancelMutation.graphql';
import { TritxApplyPresentationalContent } from '@/components/tax-refund/apply/result/template/v-20260211/TritxApplyPresentationalContent';
import { checkTritxRefund } from '@/components/tax-refund/common/graphql/query/checkTritxRefund';
import { tritxRevokeCancel } from '@/graphql/mutation/taxRefund/tritxRevokeCancel';
import { COMMON_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { useApplyResultData } from '@/lib/hooks/use-apply-result-data';
import { cancelDataAtom } from '@/lib/stores/cancel-data';

const TritxApplyRefundResultContent = () => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const { toast } = useToast();
  const { applyResultData, setDisabledCancelButton, setDisabledRevokeButton } = useApplyResultData('indis');
  const relayEnvironment = useRelayEnvironment();
  const setCancelData = useSetAtom(cancelDataAtom);

  const handleCardClick = () => {
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const handleClickCancel = async () => {
    setDisabledCancelButton(true);
    const data = await checkTritxRefund(relayEnvironment, applyResultData?.tritxRefundInfo?.refundId ?? '');
    if (data?.cancelStage !== 'Canceling' && data?.refundStatus === 'ApplyComplete' && data?.refundStage === applyResultData?.tritxRefundInfo?.refundStage) {
      setCancelData({
        refundId: applyResultData?.tritxRefundInfo?.refundId ?? '',
        name: applyResultData?.tritxRefundInfo?.businessInfo?.taxpayerName ?? '',
        businessType: 'indis'
      });
      router.push({
        pathname: `${INDIVIDUAL_PATHS.개인취소방어}`,
        query: { ...router.query, type: 'indis-tritx' }
      });
    } else {
      showDialog({
        title: '진행 단계에 변동이 있어 새로고침이 필요해요',
        description: '확인을 누르시면 현재 상태로 페이지가 새로고침돼요.',
        buttons: {
          primary: {
            onClick: () => {
              router.reload();
            }
          }
        }
      });
    }

    setDisabledCancelButton(false);
  };

  const [handleTritxRevokeCancel] = useMutation<tritxRevokeCancelMutation>(tritxRevokeCancel);

  const restartRefund = () => {
    handleTritxRevokeCancel({
      variables: {
        refundId: applyResultData?.tritxRefundInfo?.refundId ?? ''
      },
      onCompleted: (response) => {
        if (response.tritxRevokeCancel.result) {
          showDialog({
            title: '이제 환급이 다시 진행돼요',
            description: '신속하고 빠짐없이 환급받으실 수 있도록 최선을 다할게요.',
            buttons: {
              primary: {
                onClick: () => {
                  window.location.reload();
                }
              }
            }
          });
        } else {
          setDisabledRevokeButton(false);
          toast({
            title: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.',
            variant: 'warning'
          });
        }
      },
      onError: (error) => {
        console.log(error);
        setDisabledRevokeButton(false);
        toast({
          title: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.',
          variant: 'warning'
        });
      }
    });
  };

  const handleRevokeClick = async () => {
    setDisabledRevokeButton(true);

    const data = await checkTritxRefund(relayEnvironment, applyResultData?.tritxRefundInfo?.refundId ?? '');
    if (data?.cancelStage === 'Canceling' && data?.refundStatus === 'ApplyComplete' && (data?.refundStage === 'Review' || data?.refundStage === 'Declare')) {
      showDialog({
        title: '환급을 다시 진행할까요?',
        description: '재조회 없이 진행했던 단계부터 다시 환급 진행이 가능해요.',
        buttons: {
          primary: {
            text: '재진행',
            onClick: () => {
              restartRefund();
            }
          },
          secondary: {
            onClick: () => {
              setDisabledRevokeButton(false);
            }
          }
        }
      });
    } else {
      showDialog({
        title: '진행 단계에 변동이 있어 새로고침이 필요해요',
        description: '확인을 누르시면 현재 상태로 페이지가 새로고침돼요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              window.location.reload();
            }
          }
        }
      });
    }
  };

  return (
    <TritxApplyPresentationalContent
      data={applyResultData}
      handleCardClick={handleCardClick}
      handleClickCancel={handleClickCancel}
      handleRevokeClick={handleRevokeClick}
    />
  );
};

export default TritxApplyRefundResultContent;
